import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { OrgUnitDto, UpdateOrgUnitInput } from '@eombor/contracts';
import { Button, Dialog, Input } from '@eombor/ui';
import { zodResolver } from '../../../shared/form/zod-resolver.js';
import { t } from '../../../shared/i18n/index.js';

/**
 * Korxonaning **kodi va turi o'zgarmaydi** — backenddagi `updateOrgUnitSchema`
 * ham faqat nom va faollikni qabul qiladi. Kod hujjatlarda, etiketkalarda va
 * boshqa tizimlarda qolgani uchun uni tahrirlash mumkin emas.
 */
export const orgUnitFormSchema = z.object({
  name: z.string().min(2, t('org.errorName')).max(200),
  isActive: z.boolean(),
});

export type OrgUnitFormValues = z.infer<typeof orgUnitFormSchema>;

type Assert<T extends true> = T;
export type OrgUnitFormMatchesContract = Assert<
  OrgUnitFormValues extends UpdateOrgUnitInput ? true : false
>;

export interface OrgUnitDialogProps {
  open: boolean;
  onClose: () => void;
  unit: OrgUnitDto | null;
  onSubmit: (values: OrgUnitFormValues) => void;
  pending?: boolean;
  error?: string | null;
}

export const OrgUnitDialog = ({
  open,
  onClose,
  unit,
  onSubmit,
  pending = false,
  error = null,
}: OrgUnitDialogProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrgUnitFormValues>({
    resolver: zodResolver(orgUnitFormSchema),
    defaultValues: { name: '', isActive: true },
  });

  useEffect(() => {
    if (!open || unit === null) return;
    reset({ name: unit.name, isActive: unit.isActive });
  }, [open, unit, reset]);

  const submit = handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('org.dialogEditTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('org.cancel')}
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            {t('org.save')}
          </Button>
        </>
      }
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {error !== null ? (
          <p
            role="alert"
            className="rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        ) : null}

        {unit === null ? null : (
          <p className="tabular text-xs text-text-faint">
            {unit.code} · {t('org.fieldType')}: {unit.type}
          </p>
        )}

        <Input label={t('org.fieldName')} error={errors.name?.message} {...register('name')} />

        <label className="flex cursor-pointer items-center gap-2 text-sm text-text">
          <input
            type="checkbox"
            className="size-4 accent-accent"
            {...register('isActive')}
          />
          {t('org.statusActive')}
        </label>

        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden>
          {t('org.save')}
        </button>
      </form>
    </Dialog>
  );
};
